import React, { useCallback } from "react";
import { Package, Plus, Search, Trash2, ChevronDown } from "lucide-react";
import TotalsCard from "./TotalsCard";

export default function ArticulosSection({
  datos,
  addArticulo,
  eliminar,
  actualizarCantidad,
  handleInputChange,
  setDatos,
  markDirty,
  subtotalBase,
  iva,
  total,
  ivaPct,
  divisa,
}) {
  const isEmpty = !datos || datos.length === 0;

  const onPrecioChange = useCallback(
    (id, raw) => {
      if (raw === "") {
        setDatos?.((prev) =>
          (Array.isArray(prev) ? prev : []).map((it) =>
            it.id === id ? { ...it, tarifa: "" } : it,
          ),
        );
        markDirty?.();
        return;
      }

      const next = Number(raw);
      const safeVal = Number.isFinite(next) ? next : 0;

      setDatos?.((prev) =>
        (Array.isArray(prev) ? prev : []).map((it) =>
          it.id === id ? { ...it, tarifa: safeVal } : it,
        ),
      );
      markDirty?.();
    },
    [markDirty, setDatos],
  );

  const onDescuentoChange = useCallback(
    (id, raw) => {
      const v = raw ?? "";
      let cleaned = v.replace(/[^0-9.]/g, "");

      const firstDot = cleaned.indexOf(".");
      if (firstDot !== -1) {
        cleaned =
          cleaned.slice(0, firstDot + 1) +
          cleaned.slice(firstDot + 1).replace(/\./g, "");
      }

      if (cleaned.startsWith("0") && cleaned.length > 1 && cleaned[1] !== ".") {
        cleaned = cleaned.replace(/^0+/, "");
        if (cleaned === "") cleaned = "0";
      }

      const nextVal = cleaned === "" ? 0 : Number(cleaned);
      const safeVal = Number.isFinite(nextVal) ? nextVal : 0;

      setDatos?.((prev) =>
        (Array.isArray(prev) ? prev : []).map((it) =>
          it.id === id ? { ...it, descuento: safeVal } : it,
        ),
      );
      markDirty?.();
    },
    [markDirty, setDatos],
  );

  return (
    <>
      {isEmpty ? (
        <div className="cf-empty2">
          <div className="cf-empty2-icon" aria-hidden="true">
            <Package size={24} />
          </div>

          <p className="cf-empty2-text">
            No hay conceptos agregados. Haz clic en &quot;Agregar Concepto&quot;
            para comenzar.
          </p>

          <button type="button" className="cf-primary" onClick={addArticulo}>
            <Plus size={16} /> Agregar Concepto
          </button>
        </div>
      ) : (
        <>
          <div className="cf-items">
            {datos.map((p, idx) => {
              const tarifa = p?.tarifa === "" ? 0 : Number(p?.tarifa || 0);
              const cantidad = Number(p?.cantidad || 0);
              const base = tarifa * cantidad;

              const d = Number(p?.descuento || 0);
              const tipo = p?.tipoDescuento || "%";

              let descLinea = tipo === "%" ? base * (d / 100) : d;
              if (descLinea > base) descLinea = base;

              const totalLinea = Math.max(0, base - descLinea);

              return (
                <div key={p.id} className="cf-item-card">
                  <div className="cf-item-head">
                    <span className="cf-item-title">Concepto #{idx + 1}</span>

                    <button
                      type="button"
                      className="cf-item-trash"
                      onClick={() => {
                        eliminar?.(p.id);
                        markDirty?.();
                      }}
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="cf-item-grid">
                    <div className="cf-item-row">
                      <div className="cf-span-6">
                        <label className="cf-label-sm">
                          Nombre <span className="cf-required">*</span>
                        </label>

                        <div className="cf-input-wrap">
                          <input
                            className="cf-input cf-input-sm cf-input-iconpad"
                            name="nombre"
                            value={p.nombre || ""}
                            onChange={(e) => {
                              handleInputChange?.(e, p.id);
                              markDirty?.();
                            }}
                            placeholder="Nombre del producto"
                          />
                          <Search size={16} className="cf-input-icon" />
                        </div>
                      </div>

                      <div className="cf-span-2">
                        <label className="cf-label-sm">SKU</label>
                        <input
                          className="cf-input cf-input-sm"
                          name="sku"
                          value={p.sku || ""}
                          onChange={(e) => {
                            handleInputChange?.(e, p.id);
                            markDirty?.();
                          }}
                          placeholder="Código"
                        />
                      </div>

                      <div className="cf-span-2">
                        <label className="cf-label-sm">
                          Cantidad <span className="cf-required">*</span>
                        </label>
                        <input
                          className="cf-input cf-input-sm"
                          type="number"
                          min="0"
                          value={Number(p.cantidad ?? 1)}
                          onChange={(e) => {
                            actualizarCantidad?.(
                              p.id,
                              Number(e.target.value || 0),
                            );
                            markDirty?.();
                          }}
                          placeholder="1"
                        />
                      </div>

                      <div className="cf-span-2">
                        <label className="cf-label-sm">Unidad</label>

                        <div className="cf-select-wrap">
                          <select
                            className="cf-select cf-select-sm cf-select--chev"
                            value={p.unidad || "Pieza"}
                            onChange={(e) => {
                              const fakeEvent = {
                                target: {
                                  name: "unidad",
                                  value: e.target.value,
                                },
                              };
                              handleInputChange?.(fakeEvent, p.id);
                              markDirty?.();
                            }}
                          >
                            <option value="Pieza">Pieza</option>
                            <option value="PZA">PZA - Pieza</option>
                            <option value="KG">KG - Kilogramo</option>
                            <option value="GR">GR - Gramo</option>
                            <option value="TON">TON - Tonelada</option>
                            <option value="M">M - Metro</option>
                            <option value="CM">CM - Centímetro</option>
                            <option value="M2">M2 - Metro Cuadrado</option>
                            <option value="M3">M3 - Metro Cúbico</option>
                            <option value="LT">LT - Litro</option>
                            <option value="ML">ML - Mililitro</option>
                            <option value="HR">HR - Hora</option>
                            <option value="MIN">MIN - Minuto</option>
                            <option value="KWH">KWH - Kilowatt Hora</option>
                            <option value="SER">SER - Servicio</option>
                            <option value="ACT">ACT - Actividad</option>
                            <option value="PAR">PAR - Par</option>
                            <option value="JGO">JGO - Juego</option>
                            <option value="SET">SET - Set</option>
                            <option value="CJA">CJA - Caja</option>
                          </select>
                          <ChevronDown size={16} className="cf-select-chev" />
                        </div>
                      </div>
                    </div>

                    <div className="cf-item-row">
                      <div className="cf-span-6">
                        <label className="cf-label-sm">Descripción</label>
                        <textarea
                          className="cf-textarea cf-textarea-sm"
                          name="descripcion"
                          value={p.descripcion || ""}
                          onChange={(e) => {
                            handleInputChange?.(e, p.id);
                            markDirty?.();
                          }}
                          placeholder="Descripción del producto o servicio"
                          rows={2}
                        />
                      </div>

                      <div className="cf-span-2">
                        <label className="cf-label-sm">
                          Precio Unitario <span className="cf-required">*</span>
                        </label>
                        <input
                          className="cf-input cf-input-sm"
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          name="tarifa"
                          value={
                            p?.tarifa === "" ? "" : String(p?.tarifa ?? "")
                          }
                          onChange={(e) => onPrecioChange(p.id, e.target.value)}
                          placeholder="0.00"
                        />
                      </div>

                      <div className="cf-span-2">
                        <label className="cf-label-sm">Descuento</label>

                        <div className="cf-discount2">
                          <input
                            className="cf-discount2-input"
                            type="text"
                            inputMode="decimal"
                            name="descuento"
                            value={
                              Number(p?.descuento || 0) === 0
                                ? ""
                                : String(p.descuento)
                            }
                            onChange={(e) =>
                              onDescuentoChange(p.id, e.target.value)
                            }
                            placeholder="0"
                          />

                          <div className="cf-discount2-suffix-compact">
                            <span className="cf-discount2-value">
                              {p.tipoDescuento || "%"}
                            </span>

                            <select
                              className="cf-discount2-select"
                              name="tipoDescuento"
                              value={p.tipoDescuento || "%"}
                              onChange={(e) => {
                                handleInputChange?.(e, p.id);
                                markDirty?.();
                              }}
                              aria-label="Tipo de descuento"
                            >
                              <option value="%">%</option>
                              <option value="$">$</option>
                            </select>

                            <ChevronDown
                              size={14}
                              className="cf-discount2-chev"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="cf-span-2">
                        <label className="cf-label-sm">Total</label>
                        <input
                          className="cf-input cf-input-sm cf-input-disabled-right"
                          disabled
                          value={`$${totalLinea.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button type="button" className="cf-add-dashed" onClick={addArticulo}>
            <Plus size={16} /> Agregar Artículo
          </button>

          <TotalsCard
            datos={datos}
            subtotalBase={subtotalBase}
            iva={iva}
            total={total}
            ivaPct={ivaPct}
            divisa={divisa}
          />
        </>
      )}
    </>
  );
}
